/* eslint-disable max-lines */
// Service-page translations (EN + NL). One dictionary per language.
// Keys map to the 5 service routes. Each service has the full content shape
// expected by ServicePageTemplate.

export const servicePagesEn = {
  air_freight: {
    seo: {
      title: "Air Freight Netherlands → Curaçao | 3–5 Day Delivery | 5999Cargo",
      description:
        "Fast, secure air freight from the Netherlands to Curaçao and the Caribbean. Door-to-door in 3–5 days. Get a quote within 1 business hour.",
      keywords: "air freight Netherlands Curaçao, urgent shipping Caribbean, fast cargo Rotterdam Willemstad",
    },
    eyebrow: "/ Air Freight",
    title: "Urgent shipments,",
    highlight: "airborne in 3–5 days.",
    intro:
      "When time matters more than cost. Our air freight service moves your goods from any Dutch address to Curaçao and other Caribbean destinations in 3–5 business days, with full tracking and insurance.",
    benefits: [
      { title: "3–5 day delivery", desc: "Direct cargo flights from AMS/Schiphol with onward delivery on Curaçao." },
      { title: "Up to 1,000 kg per shipment", desc: "Single packages or palletized cargo. Heavier? Talk to us about charters." },
      { title: "Real-time tracking", desc: "Reference number issued at pickup. Updates via WhatsApp or email." },
      { title: "Customs included", desc: "We prepare CN23 forms and Curaçao import declaration." },
      { title: "Insurance available", desc: "Optional all-risk insurance from 1.5% of declared value." },
      { title: "Pickup anywhere in NL", desc: "Same-day pickup in Rotterdam region, 1–3 days nationwide." },
    ],
    pricing: [
      {
        name: "Documents & Small",
        price: "€69",
        unit: "/ up to 5 kg",
        note: "Letters, USB drives, electronics, jewelry, samples.",
        features: ["Door-to-door 3–5 days", "Tracking included", "Up to €500 declared value"],
      },
      {
        name: "Standard Box",
        price: "€189",
        unit: "/ up to 25 kg",
        featured: true,
        note: "Most popular for personal items, e-commerce or gifts.",
        features: ["Door-to-door 3–5 days", "Tracking + WhatsApp updates", "Customs paperwork included"],
      },
      {
        name: "Pallet / Bulk",
        price: "€2.90",
        unit: "/ kg from 100 kg",
        note: "Pallets up to 1,000 kg per shipment.",
        features: ["Volumetric weight applied", "Pallet pickup included", "Custom Incoterms (DAP/DDP)"],
      },
    ],
    steps: [
      { title: "Quote in 1 hour", description: "Send dimensions and weight. Quote and pickup window confirmed." },
      { title: "Pickup at your door", description: "Our courier collects in NL within agreed window." },
      { title: "Direct flight", description: "Cargo loaded on the next available flight to Curaçao." },
      { title: "Delivered on island", description: "Last-mile delivery to recipient with signature." },
    ],
    faq: [
      { q: "What can I NOT send by air?", a: "Lithium batteries above thresholds, flammable liquids, perishables without cooling, restricted items per IATA. We screen every shipment." },
      { q: "Can I add insurance?", a: "Yes — optional all-risk insurance from 1.5% of declared value, capped at €25,000 per shipment." },
      { q: "Is pickup included in the price?", a: "Yes for Rotterdam region. Other NL postal codes: small surcharge based on distance, shown upfront in your quote." },
      { q: "Do you deliver to Aruba and Bonaire?", a: "Yes, full Caribbean coverage including Aruba, Bonaire, St. Maarten and Suriname." },
    ],
    bottomCta: {
      title: "Need it there next week?",
      subtitle: "Air freight requests booked before 14:00 NL time go on the next outbound flight.",
      primaryLabel: "Book air freight",
    },
  },

  sea_freight: {
    seo: {
      title: "Sea Freight Netherlands → Curaçao | Affordable Container Shipping | 5999Cargo",
      description:
        "Affordable sea freight from Rotterdam to Curaçao and the Caribbean. Full and shared containers, 12–18 day transit. Get a transparent quote.",
      keywords: "sea freight Netherlands Curaçao, container shipping Rotterdam Caribbean, LCL FCL Willemstad",
    },
    eyebrow: "/ Sea Freight",
    title: "Big shipments,",
    highlight: "smartly priced by sea.",
    intro:
      "The most cost-effective way to move household goods, vehicles, e-commerce inventory, and bulk cargo from the Netherlands to Curaçao. Shared (LCL) or full container (FCL) — we handle both, including customs.",
    benefits: [
      { title: "Up to 60% cheaper than air", desc: "Best per-kg/m³ rate for any shipment over 50 kg." },
      { title: "Weekly departures from Rotterdam", desc: "Vessels leave every week. Cut-off Friday 14:00." },
      { title: "Shared (LCL) or full (FCL)", desc: "Pay for what you ship. Or take a full 20'/40' container." },
      { title: "Door-to-door option", desc: "Pickup, customs, and last-mile in one price." },
      { title: "Vehicle shipping", desc: "Cars, motorcycles, boats — we handle Ro-Ro and container." },
      { title: "Transit 12–18 days", desc: "Direct routes Rotterdam → Willemstad. No transshipment." },
    ],
    pricing: [
      {
        name: "Personal box",
        price: "€95",
        unit: "/ up to 25 kg / 0.1 m³",
        note: "Single boxes, gifts to family, personal items.",
        features: ["Door-to-door 12–18 days", "Customs included", "WhatsApp updates"],
      },
      {
        name: "Shared container (LCL)",
        price: "€295",
        unit: "/ m³ (min 1 m³)",
        featured: true,
        note: "Most popular for partial moves and small business.",
        features: ["Pay only for your volume", "Pickup anywhere in NL", "Insurance optional"],
      },
      {
        name: "Full container (FCL)",
        price: "from €4,200",
        unit: "/ 20-foot",
        note: "Full house relocations, large e-commerce inventory, vehicles.",
        features: ["20' or 40' available", "Live tracking", "Custom Incoterms"],
      },
    ],
    steps: [
      { title: "Free volume estimate", description: "Send a list of items. We calculate cubic meters and quote." },
      { title: "Pickup & packing", description: "Optional packing service. We collect from your address." },
      { title: "Container loading", description: "Loaded at our Rotterdam warehouse, sealed and shipped." },
      { title: "Cleared & delivered", description: "We clear customs in Curaçao and deliver to your door." },
    ],
    faq: [
      { q: "How do I know how much volume I have?", a: "We have a simple volume calculator — or share photos of your items and our team estimates within an hour." },
      { q: "Can I ship a car?", a: "Yes — cars, motorcycles, jet skis, boats. We use container loading for safer transport." },
      { q: "Are there items I cannot send by sea?", a: "Hazardous goods, flammables, perishables without cooling. We will flag any restricted item before pickup." },
      { q: "How is the price calculated?", a: "By cubic meter (m³) or per kg, whichever is greater. Shared containers (LCL) are billed per m³ from a 1 m³ minimum." },
      { q: "What about insurance?", a: "Optional all-risk insurance from 2% of declared value, with a clear claim process." },
    ],
    bottomCta: {
      title: "Moving more than 5 m³?",
      subtitle: "We give a fixed all-in quote for full container shipments — pickup, customs and delivery included.",
      primaryLabel: "Request container quote",
    },
  },

  moving: {
    seo: {
      title: "Moving to Curaçao from Netherlands | Full Relocation Service | 5999Cargo",
      description:
        "Relocating from the Netherlands to Curaçao? Our full-service movers handle packing, customs and last-mile delivery. Get a free moving quote.",
      keywords: "moving to Curaçao cost, relocation Netherlands Caribbean, household movers Rotterdam Willemstad",
    },
    eyebrow: "/ Moving & Relocation",
    title: "Your move to Curaçao,",
    highlight: "handled door-to-door.",
    intro:
      "Relocating an entire household across the Atlantic is stressful. We make it simple: professional packing in NL, sea container shipping, customs handling and delivery to your new home in Curaçao.",
    benefits: [
      { title: "Professional packing", desc: "Trained crew, premium materials, careful labeling." },
      { title: "Customs handled", desc: "We prepare your inventory and customs declaration for Curaçao." },
      { title: "Storage available", desc: "Free 14 days storage in NL or Curaçao if your home isn't ready." },
      { title: "Furniture disassembly", desc: "Beds, wardrobes, dining tables — we take apart and reassemble." },
      { title: "Pet-friendly partners", desc: "Trusted partners for relocating pets to Curaçao." },
      { title: "All-in fixed price", desc: "No surprises — one quote covers everything from origin to destination." },
    ],
    pricing: [
      {
        name: "Studio / 1-bedroom",
        price: "€1,950",
        unit: "all-in",
        note: "≈ 5 m³. Single person or couple's essentials.",
        features: ["Pickup in NL", "Sea freight 14 days", "Customs + delivery"],
      },
      {
        name: "Family — 3 bedroom",
        price: "€3,750",
        unit: "all-in",
        featured: true,
        note: "≈ 15 m³. Most common family relocation.",
        features: ["Professional packing", "Sea freight 14 days", "Free 14-day storage"],
      },
      {
        name: "Large home / Villa",
        price: "from €5,900",
        unit: "20' container",
        note: "Full container — fits 25–30 m³.",
        features: ["Dedicated 20' container", "Door-to-door", "Insurance optional"],
      },
    ],
    steps: [
      { title: "Free home survey", description: "We visit (or video-call) to estimate volume and quote." },
      { title: "Packing day in NL", description: "Our crew packs and labels everything carefully." },
      { title: "Sea container shipment", description: "Goods sealed in container and shipped to Curaçao." },
      { title: "Unpack at your new home", description: "Reassembly and placement at your new address on the island." },
    ],
    faq: [
      { q: "How long does a full move take?", a: "Door-to-door 18–24 days from pickup to delivery in Curaçao, including customs clearance." },
      { q: "Can I take my car?", a: "Yes — cars are commonly added to family container shipments. We handle import paperwork." },
      { q: "What about my electronics?", a: "All standard 220V Dutch appliances work in Curaçao. We pack them safely." },
      { q: "Do you do packing only?", a: "Yes — packing-only or container-only options are available if you prefer to manage parts yourself." },
      { q: "Can I store my things in Curaçao?", a: "Yes — secure climate-controlled storage available before delivery to your new home." },
    ],
    bottomCta: {
      title: "Planning your move? Talk to a relocation specialist.",
      subtitle: "Free 30-minute consultation. We answer customs, timing, and packing questions specific to your situation.",
      primaryLabel: "Book a consultation",
    },
  },

  shop_drop_ship: {
    seo: {
      title: "Shop, Drop & Ship — Buy in NL, Receive in Curaçao | 5999Cargo",
      description:
        "Get a free Rotterdam shipping address. Order from any Dutch webshop (Bol, Coolblue, Zalando), we consolidate and forward to Curaçao at unbeatable rates.",
      keywords: "shop drop ship Curaçao, package forwarding Netherlands, Bol Coolblue Curaçao, Dutch webshop shipping Caribbean",
    },
    eyebrow: "/ Shop, Drop & Ship",
    title: "Shop in Holland.",
    highlight: "Receive in Curaçao.",
    intro:
      "Dutch retailers like Bol.com, Coolblue, Zalando and MediaMarkt won't ship to Curaçao. We solve that. Get your free 5999Cargo Rotterdam address, shop anywhere in the Netherlands, and we'll consolidate and forward your packages to the island.",
    benefits: [
      { title: "Free Rotterdam address", desc: "Use it on any Dutch webshop. Sign up takes 2 minutes." },
      { title: "Free 30-day storage", desc: "We hold your packages until you're ready to ship them all together." },
      { title: "Save up to 60%", desc: "Combine 10 small packages into one shipment, pay one shipping fee." },
      { title: "Photo on arrival", desc: "We photograph each package received, you confirm before shipping." },
      { title: "Personal shopper option", desc: "Don't have a NL bank card? We can buy on your behalf." },
      { title: "Customs sorted", desc: "We declare your goods correctly to avoid Curaçao customs surprises." },
    ],
    pricing: [
      {
        name: "Receive only",
        price: "Free",
        unit: "",
        note: "Get your address, receive packages. Pay only when you ship.",
        features: ["Free 30-day storage", "Photo on arrival", "Email confirmation"],
      },
      {
        name: "Consolidate & ship",
        price: "€95",
        unit: "/ 25 kg sea freight",
        featured: true,
        note: "Most popular — combine your purchases into one box.",
        features: ["Sea freight 14 days", "Customs included", "Up to 25 kg per box"],
      },
      {
        name: "Personal shopper",
        price: "+8%",
        unit: "of order value",
        note: "We buy on your behalf using your shopping list.",
        features: ["Pay us, we order", "Verify items before shipping", "Refund handling"],
      },
    ],
    steps: [
      { title: "Sign up", description: "Get your unique 5999Cargo Rotterdam shipping address. Free." },
      { title: "Order from any Dutch shop", description: "Use the address as the delivery address on Bol, Coolblue, etc." },
      { title: "We receive & consolidate", description: "We receive, photograph, and consolidate your packages safely." },
      { title: "Ship to Curaçao", description: "When ready, click ship — we send everything in one go." },
    ],
    faq: [
      { q: "Is the address really free?", a: "Yes — receiving and storing for 30 days is free. You only pay when you ship to Curaçao." },
      { q: "How long is free storage?", a: "30 days. After that, a small daily storage fee applies (€0.50/box/day). Most customers ship within 1–2 weeks." },
      { q: "Can I use it for returns?", a: "Yes — if a Dutch retailer requires a NL return address for refunds, our address works perfectly." },
      { q: "What if my package is lost or damaged?", a: "We photograph everything on arrival. Damaged-on-arrival is reported back to the shop. We're not the shop's carrier — but we help." },
      { q: "Can I use it as a NL business address?", a: "No — our service is for parcel receiving only, not registered business addresses." },
    ],
    bottomCta: {
      title: "Ready to get your free address?",
      subtitle: "Sign up takes 2 minutes — start shopping Holland today and ship to Curaçao tomorrow.",
      primaryLabel: "Get my free address",
    },
  },

  shipping_to_curacao: {
    seo: {
      title: "Shipping to Curaçao from the Netherlands | All-in-One Guide | 5999Cargo",
      description:
        "The complete guide to shipping packages, freight or your household to Curaçao from the Netherlands. Pricing, transit times, customs and how to send.",
      keywords: "shipping to Curaçao, send package to Curaçao, freight Netherlands to Caribbean, Curaçao customs, Rotterdam Willemstad shipping",
    },
    eyebrow: "/ Shipping to Curaçao",
    title: "Everything for shipping",
    highlight: "from NL to Curaçao.",
    intro:
      "Whether you're sending a single package to family in Otrobanda, moving an entire household to Willemstad, or running an e-commerce store that ships to the island — this is the only logistics partner you need in Rotterdam.",
    benefits: [
      { title: "Direct to Curaçao", desc: "No transshipment via Miami or Panama. Direct routes Rotterdam → Willemstad." },
      { title: "Native customs experience", desc: "Our team speaks Papiamento, Dutch and English. We know Curaçao customs inside-out." },
      { title: "Door-to-door anywhere on the island", desc: "Willemstad, Punda, Otrobanda, Westpunt — we deliver to every postal code." },
      { title: "Air or sea options", desc: "3–5 days by air, or 12–18 days by sea. Choose what fits your budget and urgency." },
      { title: "Track everything", desc: "Reference number on every shipment, with WhatsApp updates when you want them." },
      { title: "Pay how you like", desc: "iDEAL, credit card, bank transfer or our online payment links." },
    ],
    pricing: [
      {
        name: "Single box (sea)",
        price: "€95",
        unit: "/ up to 25 kg",
        note: "Personal items, gifts, e-commerce orders to family.",
        features: ["Door-to-door 14 days", "Customs included", "Track via WhatsApp"],
      },
      {
        name: "Air freight box",
        price: "€189",
        unit: "/ up to 25 kg",
        featured: true,
        note: "Urgent items in 3–5 business days.",
        features: ["Direct flight Schiphol → CUR", "Tracking included", "Insurance optional"],
      },
      {
        name: "Full move",
        price: "from €1,950",
        unit: "all-in",
        note: "Studios from €1,950, family homes from €3,750.",
        features: ["Pickup, customs, delivery", "Container shipping", "Free 14-day storage"],
      },
    ],
    steps: [
      { title: "Get a quote", description: "Tell us what you're shipping. Reply within 1 business hour." },
      { title: "Drop or pickup", description: "Drop at our Rotterdam warehouse or schedule pickup nationwide NL." },
      { title: "We ship and clear", description: "Air or sea. Customs cleared on Curaçao on arrival." },
      { title: "Delivered to recipient", description: "Last-mile delivery to any address on the island, with confirmation." },
    ],
    faq: [
      { q: "How much does it cost to send a box to Curaçao?", a: "From €95 by sea (25 kg, door-to-door, 14 days) or from €189 by air (25 kg, 3–5 days). All-in pricing including customs." },
      { q: "What can I send to Curaçao?", a: "Almost anything — personal items, electronics, household goods, food in proper packaging, vehicles. Restrictions apply for hazardous goods, perishables, and some plant/animal products." },
      { q: "Does Curaçao charge import duty?", a: "Items above ANG 500 may incur import duty (typically 6–22%). We help you declare correctly to minimize duty where legal." },
      { q: "How do I pay?", a: "iDEAL, credit card via Stripe, or bank transfer. Payment links sent by email." },
      { q: "What's the cheapest way to send to Curaçao?", a: "Sea freight in our shared container — €95 for a 25 kg box, 14 days door-to-door including customs." },
    ],
    bottomCta: {
      title: "Send to Curaçao the easy way.",
      subtitle: "Quote in 1 hour, pickup in 24h, delivery in 3–14 days.",
    },
  },
};

export const servicePagesNl = {
  air_freight: {
    seo: {
      title: "Luchtvracht naar Curaçao & Caribbean | Snelle airfreight | 5999Cargo",
      description:
        "Snelle luchtvracht vanuit Nederland naar Curaçao, Aruba, Bonaire, Sint Maarten en Suriname. Pakketten, documenten, onderdelen en spoedzendingen — met online paklijst, Mijn 5999Cargo en duidelijke documentcontrole vóór vertrek.",
      keywords: "luchtvracht Curaçao, luchtvracht naar Aruba, pakket versturen Curaçao luchtvracht, spoedzending Caribbean, airfreight Bonaire, snelle vracht Caribbean, 5999Cargo luchtvracht",
    },
    eyebrow: "/ Luchtvracht",
    title: "Luchtvracht naar de Caribbean —",
    highlight: "snel, gecontroleerd, online geregeld.",
    intro:
      "Luchtvracht is dé keuze voor pakketten, documenten, onderdelen, monsters en spoedzendingen. 5999Cargo verstuurt vanuit Nederland naar Curaçao, Aruba, Bonaire, Sint Maarten en Suriname — meestal binnen 5–7 werkdagen. Vul uw paklijst online in, upload uw factuur en betaal vóór vertrek. Géén complete documenten of betaling betekent géén verzending — zo voorkomen we vertraging in de haven en bij de douane.",
    benefits: [
      { title: "Levering in 3–5 dagen", desc: "Directe vrachtvluchten vanaf Schiphol met aansluitende bezorging op Curaçao." },
      { title: "Tot 1.000 kg per zending", desc: "Losse pakketten of pallets. Zwaarder? Vraag naar onze charters." },
      { title: "Real-time tracking", desc: "Referentienummer bij ophalen. Updates via WhatsApp of e-mail." },
      { title: "Inclusief douane", desc: "Wij verzorgen CN23-formulieren en de Curaçaose invoeraangifte." },
      { title: "Verzekering mogelijk", desc: "Optionele all-risk verzekering vanaf 1,5% van de aangegeven waarde." },
      { title: "Ophalen heel NL", desc: "Zelfde dag in regio Rotterdam, 1–3 dagen landelijk." },
    ],
    pricing: [
      {
        name: "Documenten & klein",
        price: "€69",
        unit: "/ tot 5 kg",
        note: "Brieven, USB-sticks, elektronica, sieraden, monsters.",
        features: ["Deur tot deur 3–5 dagen", "Tracking inbegrepen", "Tot €500 aangegeven waarde"],
      },
      {
        name: "Standaarddoos",
        price: "€189",
        unit: "/ tot 25 kg",
        featured: true,
        note: "Meest gekozen voor persoonlijke items, e-commerce of cadeaus.",
        features: ["Deur tot deur 3–5 dagen", "Tracking + WhatsApp updates", "Inclusief douanepapieren"],
      },
      {
        name: "Pallet / Bulk",
        price: "€2,90",
        unit: "/ kg vanaf 100 kg",
        note: "Pallets tot 1.000 kg per zending.",
        features: ["Volumetrisch gewicht van toepassing", "Inclusief palletophaling", "Maatwerk Incoterms (DAP/DDP)"],
      },
    ],
    steps: [
      { title: "Offerte binnen 1 uur", description: "Stuur afmetingen en gewicht. Offerte en ophaalmoment bevestigd." },
      { title: "Ophalen aan huis", description: "Onze koerier haalt op binnen het afgesproken venster." },
      { title: "Directe vlucht", description: "Vracht op de eerstvolgende vlucht naar Curaçao." },
      { title: "Bezorgd op het eiland", description: "Laatste mijl bezorging bij ontvanger met handtekening." },
    ],
    faq: [
      { q: "Wat mag NIET per luchtvracht?", a: "Lithiumbatterijen boven de drempelwaarden, brandbare vloeistoffen, bederfelijke waar zonder koeling, beperkte items volgens IATA. Wij screenen elke zending." },
      { q: "Kan ik een verzekering toevoegen?", a: "Ja — optionele all-risk verzekering vanaf 1,5% van de aangegeven waarde, tot maximaal €25.000 per zending." },
      { q: "Is ophalen inbegrepen in de prijs?", a: "Ja voor regio Rotterdam. Andere postcodes: kleine toeslag op basis van afstand, vooraf zichtbaar in je offerte." },
      { q: "Bezorgen jullie ook op Aruba en Bonaire?", a: "Ja, volledige Caribische dekking inclusief Aruba, Bonaire, Sint Maarten en Suriname." },
    ],
    bottomCta: {
      title: "Volgende week op locatie nodig?",
      subtitle: "Luchtvrachtaanvragen voor 14:00 NL-tijd vertrekken op de eerstvolgende vlucht.",
      primaryLabel: "Boek luchtvracht",
    },
  },

  sea_freight: {
    seo: {
      title: "Zeevracht naar Curaçao & Caribbean | Container, LCL & FCL | 5999Cargo",
      description:
        "Voordelige zeevracht vanuit Rotterdam naar Curaçao, Aruba, Bonaire, Sint Maarten en Suriname. Dozen, pallets, meubels, voertuigen en zakelijke voorraad — LCL, FCL of een complete 20ft/40ft container. Met online CBM-calculator, paklijst en duidelijke documentcontrole.",
      keywords: "zeevracht Curaçao, container naar Curaçao, container naar Aruba, LCL zeevracht Caribbean, FCL Rotterdam, vracht naar Bonaire, zakelijke zending Caribbean, voertuig verschepen Curaçao",
    },
    eyebrow: "/ Zeevracht",
    title: "Zeevracht naar de Caribbean —",
    highlight: "voordelig per doos, pallet of container.",
    intro:
      "Voor dozen, pallets, meubels, voertuigen, verhuisgoederen en zakelijke voorraad is zeevracht de voordeligste keuze. Wij verschepen wekelijks vanuit Rotterdam naar Curaçao, Aruba, Bonaire, Sint Maarten en Suriname. Kies LCL (groupage, per m³), een gedeelde 20ft container of een complete FCL-container. Bereken vooraf uw volume met de CBM-calculator en gebruik onze staffeltarieven voor terugkerende cargo.",
    benefits: [
      { title: "Tot 60% goedkoper dan lucht", desc: "Beste prijs per kg/m³ voor zendingen boven 50 kg." },
      { title: "Wekelijks vertrek vanuit Rotterdam", desc: "Schepen vertrekken elke week. Cut-off vrijdag 14:00." },
      { title: "Gedeeld (LCL) of vol (FCL)", desc: "Betaal alleen voor wat je verzendt. Of neem een volledige 20'/40' container." },
      { title: "Deur-tot-deur optie", desc: "Ophalen, douane en laatste mijl in één prijs." },
      { title: "Voertuigen verschepen", desc: "Auto's, motoren, boten — wij regelen Ro-Ro en container." },
      { title: "Transit 12–18 dagen", desc: "Directe routes Rotterdam → Willemstad. Geen overslag." },
    ],
    pricing: [
      {
        name: "Persoonlijke doos",
        price: "€95",
        unit: "/ tot 25 kg / 0,1 m³",
        note: "Losse dozen, cadeaus voor familie, persoonlijke items.",
        features: ["Deur tot deur 12–18 dagen", "Inclusief douane", "WhatsApp updates"],
      },
      {
        name: "Gedeelde container (LCL)",
        price: "€295",
        unit: "/ m³ (min 1 m³)",
        featured: true,
        note: "Meest gekozen voor deelverhuizingen en kleine bedrijven.",
        features: ["Betaal alleen voor jouw volume", "Ophalen heel NL", "Verzekering optioneel"],
      },
      {
        name: "Volle container (FCL)",
        price: "vanaf €4.200",
        unit: "/ 20-voet",
        note: "Volledige verhuizingen, grote e-commerce voorraad, voertuigen.",
        features: ["20' of 40' beschikbaar", "Live tracking", "Maatwerk Incoterms"],
      },
    ],
    steps: [
      { title: "Gratis volume-inschatting", description: "Stuur een lijst met items. Wij berekenen kubieke meters en geven offerte." },
      { title: "Ophalen & inpakken", description: "Optionele inpakservice. Wij komen langs op je adres." },
      { title: "Container laden", description: "Geladen in ons depot Rotterdam, verzegeld en verscheept." },
      { title: "Ingeklaard & bezorgd", description: "Wij klaren in op Curaçao en bezorgen aan huis." },
    ],
    faq: [
      { q: "Hoe weet ik hoeveel volume ik heb?", a: "We hebben een eenvoudige volumecalculator — of stuur foto's en ons team schat binnen een uur." },
      { q: "Kan ik een auto verschepen?", a: "Ja — auto's, motoren, jetski's, boten. Wij gebruiken containertransport voor veiliger vervoer." },
      { q: "Welke items mogen niet per zee?", a: "Gevaarlijke goederen, brandbare stoffen, bederfelijke waar zonder koeling. We melden vooraf welke items beperkt zijn." },
      { q: "Hoe wordt de prijs berekend?", a: "Per kubieke meter (m³) of per kg, wat hoger is. Gedeelde containers (LCL) per m³ vanaf 1 m³ minimum." },
      { q: "Hoe zit het met verzekering?", a: "Optionele all-risk verzekering vanaf 2% van de aangegeven waarde, met duidelijk claimproces." },
    ],
    bottomCta: {
      title: "Verhuis je meer dan 5 m³?",
      subtitle: "Wij geven een vaste all-in prijs voor volle container — ophalen, douane en bezorging inbegrepen.",
      primaryLabel: "Vraag containerofferte",
    },
  },

  moving: {
    seo: {
      title: "Verhuizen naar Curaçao, Aruba & Bonaire | Emigratie & remigratie | 5999Cargo",
      description:
        "Volledige verhuizing of remigratie naar Curaçao, Aruba, Bonaire of Suriname. Inboedel, meubels, witgoed en voertuigen via zeevracht of FCL container. Met online paklijst, waardeverklaring, pickup en duidelijke documentcontrole.",
      keywords: "verhuizen naar Curaçao, emigreren Curaçao kosten, remigreren naar Aruba, verhuiscontainer Caribbean, inboedel verschepen Curaçao, FCL verhuizing Bonaire",
    },
    eyebrow: "/ Verhuizing & Emigratie",
    title: "Uw verhuizing naar de Caribbean —",
    highlight: "deur tot deur, met online overzicht.",
    intro:
      "Verhuizen of remigreren naar Curaçao, Aruba, Bonaire, Sint Maarten of Suriname? 5999Cargo regelt dozen, meubels, witgoed, persoonlijke spullen en voertuigen via zeevracht of een complete FCL container. U vult online uw paklijst en waardeverklaring in, wij plannen pickup in heel Nederland en controleren documenten vóór vertrek. Geen onverwachte douaneproblemen op het eiland.",
    benefits: [
      { title: "Professionele inpakkers", desc: "Geschoold team, premium materialen, zorgvuldige labeling." },
      { title: "Douane geregeld", desc: "Wij maken inventaris en douaneaangifte voor Curaçao." },
      { title: "Opslag beschikbaar", desc: "14 dagen gratis opslag in NL of Curaçao als je nieuwe huis nog niet klaar is." },
      { title: "Demontage van meubels", desc: "Bedden, kasten, eettafels — wij demonteren en monteren weer." },
      { title: "Huisdiervriendelijke partners", desc: "Vertrouwde partners voor het meeverhuizen van huisdieren naar Curaçao." },
      { title: "All-in vaste prijs", desc: "Geen verrassingen — één offerte dekt alles van vertrek tot bestemming." },
    ],
    pricing: [
      {
        name: "Studio / 1-slaapkamer",
        price: "€1.950",
        unit: "all-in",
        note: "≈ 5 m³. Eénpersoons of essentials voor een stel.",
        features: ["Ophalen in NL", "Zeevracht 14 dagen", "Douane + bezorging"],
      },
      {
        name: "Gezin — 3 slaapkamers",
        price: "€3.750",
        unit: "all-in",
        featured: true,
        note: "≈ 15 m³. Meest voorkomende gezinsverhuizing.",
        features: ["Professioneel inpakken", "Zeevracht 14 dagen", "14 dagen gratis opslag"],
      },
      {
        name: "Groot huis / Villa",
        price: "vanaf €5.900",
        unit: "20' container",
        note: "Volle container — past 25–30 m³.",
        features: ["Eigen 20' container", "Deur tot deur", "Verzekering optioneel"],
      },
    ],
    steps: [
      { title: "Gratis huisbezoek", description: "We bezoeken (of bellen via video) om volume in te schatten en offerte te geven." },
      { title: "Inpakdag in NL", description: "Ons team pakt en labelt alles zorgvuldig." },
      { title: "Container verzending", description: "Goederen verzegeld in container en verscheept naar Curaçao." },
      { title: "Uitpakken op nieuwe adres", description: "Hermontage en plaatsing op je nieuwe adres op het eiland." },
    ],
    faq: [
      { q: "Hoe lang duurt een volledige verhuizing?", a: "Deur tot deur 18–24 dagen vanaf ophalen tot bezorging op Curaçao, inclusief douane." },
      { q: "Kan ik mijn auto meenemen?", a: "Ja — auto's worden vaak toegevoegd aan gezinscontainerzendingen. Wij regelen de invoerpapieren." },
      { q: "Hoe zit het met mijn elektronica?", a: "Alle Nederlandse 220V apparaten werken op Curaçao. We verpakken ze veilig." },
      { q: "Doen jullie alleen inpakken?", a: "Ja — alleen-inpakken of alleen-container opties zijn beschikbaar als je delen zelf wilt regelen." },
      { q: "Kan ik mijn spullen op Curaçao opslaan?", a: "Ja — veilige klimaatgecontroleerde opslag beschikbaar voor bezorging op je nieuwe adres." },
    ],
    bottomCta: {
      title: "Plan je een verhuizing? Praat met een specialist.",
      subtitle: "Gratis 30-minuten consult. We beantwoorden vragen over douane, timing en inpakken specifiek voor jouw situatie.",
      primaryLabel: "Boek consult",
    },
  },

  shop_drop_ship: {
    seo: {
      title: "Shop, Drop & Ship — Online bestellen in Europa, ontvangen in de Caribbean | 5999Cargo",
      description:
        "Krijg een gratis Nederlands afleveradres. Bestel bij elke Europese webshop (Bol, Coolblue, Zalando, MediaMarkt, Amazon, AliExpress) en laat ons consolideren en doorsturen naar Curaçao, Aruba, Bonaire of Sint Maarten via lucht- of zeevracht. Particulier én zakelijk.",
      keywords: "Shop Drop Ship Caribbean, pakketdoorzending Curaçao, Nederlandse webshop naar Aruba, Bol naar Curaçao, Coolblue Caribbean, AliExpress doorsturen Curaçao",
    },
    eyebrow: "/ Shop, Drop & Ship",
    title: "Shop in Europa,",
    highlight: "ontvang in de Caribbean.",
    intro:
      "Veel Nederlandse en Europese webshops (Bol, Coolblue, Zalando, MediaMarkt, AliExpress) bezorgen niet rechtstreeks op Curaçao, Aruba of Bonaire. Met Shop, Drop & Ship lost u dat op: u krijgt een gratis Nederlands afleveradres bij 5999Cargo. Bestel waar u wilt, wij ontvangen, consolideren en sturen uw aankopen door — per luchtvracht of voordelig per zeevracht. Een factuur is verplicht voor de douane. Beheer alles in Mijn 5999Cargo.",
    benefits: [
      { title: "Gratis Rotterdam-adres", desc: "Te gebruiken op elke Nederlandse webshop. Aanmelden duurt 2 minuten." },
      { title: "30 dagen gratis opslag", desc: "Wij bewaren je pakketten tot je klaar bent om alles tegelijk te verzenden." },
      { title: "Bespaar tot 60%", desc: "Combineer 10 kleine pakketten tot één zending, betaal één verzendprijs." },
      { title: "Foto bij aankomst", desc: "We fotograferen elk ontvangen pakket, jij bevestigt voor verzending." },
      { title: "Personal shopper optie", desc: "Geen Nederlandse bankpas? Wij kopen voor je." },
      { title: "Douane geregeld", desc: "Wij geven je goederen correct aan zodat er geen Curaçaose douaneverrassingen zijn." },
    ],
    pricing: [
      {
        name: "Alleen ontvangen",
        price: "Gratis",
        unit: "",
        note: "Krijg je adres, ontvang pakketten. Betaal alleen bij verzenden.",
        features: ["30 dagen gratis opslag", "Foto bij aankomst", "E-mailbevestiging"],
      },
      {
        name: "Consolideren & verzenden",
        price: "€95",
        unit: "/ 25 kg zeevracht",
        featured: true,
        note: "Meest gekozen — combineer aankopen in één doos.",
        features: ["Zeevracht 14 dagen", "Inclusief douane", "Tot 25 kg per doos"],
      },
      {
        name: "Personal shopper",
        price: "+8%",
        unit: "van bestelwaarde",
        note: "Wij bestellen voor jou aan de hand van je verlanglijstje.",
        features: ["Betaal ons, wij bestellen", "Items checken voor verzending", "Refund-afhandeling"],
      },
    ],
    steps: [
      { title: "Aanmelden", description: "Krijg je unieke 5999Cargo Rotterdam-adres. Gratis." },
      { title: "Bestellen bij elke NL-shop", description: "Gebruik het adres als bezorgadres bij Bol, Coolblue, etc." },
      { title: "Wij ontvangen & consolideren", description: "Wij ontvangen, fotograferen en bundelen je pakketten veilig." },
      { title: "Verzenden naar Curaçao", description: "Klik op verzenden — wij sturen alles in één keer." },
    ],
    faq: [
      { q: "Is het adres echt gratis?", a: "Ja — ontvangen en 30 dagen opslaan is gratis. Je betaalt alleen bij verzending naar Curaçao." },
      { q: "Hoe lang gratis opslag?", a: "30 dagen. Daarna een kleine dagvergoeding (€0,50/doos/dag). De meeste klanten verzenden binnen 1–2 weken." },
      { q: "Kan ik het gebruiken voor retouren?", a: "Ja — als een Nederlandse retailer een NL-retouradres vereist voor terugbetalingen, werkt ons adres perfect." },
      { q: "Wat als mijn pakket verloren of beschadigd is?", a: "We fotograferen alles bij aankomst. Beschadigd-bij-aankomst melden we terug bij de winkel. Wij zijn niet de vervoerder van de winkel — maar we helpen." },
      { q: "Mag ik het als NL-bedrijfsadres gebruiken?", a: "Nee — onze service is alleen voor pakketontvangst, niet voor geregistreerde bedrijfsadressen." },
    ],
    bottomCta: {
      title: "Klaar voor je gratis adres?",
      subtitle: "Aanmelden duurt 2 minuten — start vandaag met shoppen in Holland en verzend morgen naar Curaçao.",
      primaryLabel: "Mijn gratis adres",
    },
  },

  shipping_to_curacao: {
    seo: {
      title: "Verzenden naar Curaçao vanuit Nederland | Compleet overzicht | 5999Cargo",
      description:
        "De complete gids voor het verzenden van pakketten, vracht of je huisraad naar Curaçao vanuit Nederland. Tarieven, doorlooptijden, douane en hoe je verzendt.",
      keywords: "verzenden naar Curaçao, pakket naar Curaçao, vracht Nederland Caribisch gebied, Curaçao douane, Rotterdam Willemstad verzending",
    },
    eyebrow: "/ Verzenden naar Curaçao",
    title: "Alles voor verzenden",
    highlight: "van NL naar Curaçao.",
    intro:
      "Of je nu een enkel pakket naar familie in Otrobanda stuurt, een hele huishouding naar Willemstad verhuist, of een e-commerce winkel runt die naar het eiland verzendt — dit is de enige logistieke partner die je nodig hebt in Rotterdam.",
    benefits: [
      { title: "Direct naar Curaçao", desc: "Geen overslag via Miami of Panama. Directe routes Rotterdam → Willemstad." },
      { title: "Native douane-ervaring", desc: "Ons team spreekt Papiamento, Nederlands en Engels. We kennen de Curaçaose douane van binnen en buiten." },
      { title: "Deur tot deur over het hele eiland", desc: "Willemstad, Punda, Otrobanda, Westpunt — we leveren op elke postcode." },
      { title: "Lucht of zee", desc: "3–5 dagen per lucht, of 12–18 dagen per zee. Kies wat past bij je budget en haast." },
      { title: "Track alles", desc: "Referentienummer op elke zending, met WhatsApp-updates wanneer je wilt." },
      { title: "Betaal hoe je wilt", desc: "iDEAL, creditcard, bankoverschrijving of onze online betaallinks." },
    ],
    pricing: [
      {
        name: "Enkele doos (zee)",
        price: "€95",
        unit: "/ tot 25 kg",
        note: "Persoonlijke items, cadeaus, e-commerce orders naar familie.",
        features: ["Deur tot deur 14 dagen", "Inclusief douane", "Track via WhatsApp"],
      },
      {
        name: "Luchtvrachtdoos",
        price: "€189",
        unit: "/ tot 25 kg",
        featured: true,
        note: "Spoedeisende items in 3–5 werkdagen.",
        features: ["Directe vlucht Schiphol → CUR", "Tracking inbegrepen", "Verzekering optioneel"],
      },
      {
        name: "Volledige verhuizing",
        price: "vanaf €1.950",
        unit: "all-in",
        note: "Studio's vanaf €1.950, gezinswoningen vanaf €3.750.",
        features: ["Ophalen, douane, bezorging", "Containertransport", "14 dagen gratis opslag"],
      },
    ],
    steps: [
      { title: "Vraag offerte", description: "Vertel ons wat je verstuurt. Antwoord binnen 1 werkuur." },
      { title: "Brengen of laten ophalen", description: "Breng naar ons depot Rotterdam of plan ophalen heel NL." },
      { title: "Wij verzenden en klaren in", description: "Lucht of zee. Douane geklaard op Curaçao bij aankomst." },
      { title: "Bezorgd bij ontvanger", description: "Laatste mijl bezorging op elk adres op het eiland, met bevestiging." },
    ],
    faq: [
      { q: "Wat kost het om een doos naar Curaçao te sturen?", a: "Vanaf €95 per zee (25 kg, deur tot deur, 14 dagen) of vanaf €189 per lucht (25 kg, 3–5 dagen). All-in inclusief douane." },
      { q: "Wat mag ik naar Curaçao sturen?", a: "Vrijwel alles — persoonlijke items, elektronica, huisraad, voedsel in juiste verpakking, voertuigen. Beperkingen gelden voor gevaarlijke goederen, bederfelijke waar en sommige plant-/dierproducten." },
      { q: "Heft Curaçao invoerheffing?", a: "Items boven ANG 500 kunnen invoerheffing krijgen (meestal 6–22%). Wij helpen je correct aan te geven om legaal te minimaliseren." },
      { q: "Hoe betaal ik?", a: "iDEAL, creditcard via Stripe, of bankoverschrijving. Betaallinks per e-mail." },
      { q: "Wat is de goedkoopste manier naar Curaçao?", a: "Zeevracht in onze gedeelde container — €95 voor een 25 kg doos, 14 dagen deur tot deur inclusief douane." },
    ],
    bottomCta: {
      title: "Verzenden naar Curaçao, simpel gemaakt.",
      subtitle: "Offerte in 1 uur, ophalen binnen 24 uur, bezorging in 3–14 dagen.",
    },
  },
};

// Common labels used by ServicePageTemplate (and possibly elsewhere).
export const servicePageCommonEn = {
  back_home: "← Back to home",
  cta_get_quote: "Get a quote",
  cta_shop_boxes: "Shop boxes",
  why_us: "/ Why us",
  why_us_title: "What you get with 5999Cargo",
  pricing: "/ Pricing",
  pricing_title: "Transparent indicative rates",
  pricing_desc:
    "All prices in EUR. Final quote depends on actual weight, volume, and customs declared value. Get an accurate quote in minutes.",
  most_popular: "Most popular",
  how_title: "How this service works",
  quote_eyebrow: "/ Quote",
  quote_title: "Tell us about your shipment.",
  quote_desc:
    "Our team replies within 1 business hour with a fixed quote. No surprises, no hidden fees.",
  cta_default_primary: "Get my quote",
};

export const servicePageCommonNl = {
  back_home: "← Terug naar home",
  cta_get_quote: "Offerte aanvragen",
  cta_shop_boxes: "Dozen kopen",
  why_us: "/ Waarom wij",
  why_us_title: "Wat krijg je bij 5999Cargo",
  pricing: "/ Tarieven",
  pricing_title: "Transparante richtprijzen",
  pricing_desc:
    "Alle prijzen in euro's. Definitieve offerte hangt af van werkelijk gewicht, volume en aangegeven douanewaarde. Krijg in minuten een nauwkeurige offerte.",
  most_popular: "Meest gekozen",
  how_title: "Zo werkt deze dienst",
  quote_eyebrow: "/ Offerte",
  quote_title: "Vertel ons over je zending.",
  quote_desc:
    "Ons team antwoordt binnen 1 werkuur met een vaste offerte. Geen verrassingen, geen verborgen kosten.",
  cta_default_primary: "Mijn offerte",
};
